import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Rocket } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  createMpReportInUnf,
  getMpReportPreview,
  updateMpReportInUnf,
} from "@/api/marketplace_reports";
import { useConnection } from "@/auth/ConnectionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRub } from "@/lib/format";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
import type {
  MpMissingProduct,
  MpReportDocumentRef,
  MpResolvedLine,
  ResolveSource,
} from "@/types/api";

function describeStatus(d: MpReportDocumentRef): {
  label: string;
  cls: string;
} {
  if (d.exists === false) {
    return { label: "нет в 1С — будет создан заново", cls: "text-rose-700" };
  }
  if (d.deletion_mark === true) {
    return { label: "помечен на удаление в 1С", cls: "text-amber-700" };
  }
  if (d.posted_in_1c === true) {
    return { label: "проведён", cls: "text-emerald-700" };
  }
  if (d.posted_in_1c === false) {
    return { label: "не проведён", cls: "text-amber-700" };
  }
  return { label: "статус в 1С не проверен", cls: "text-slate-600" };
}

function updateButtonLabel(d: MpReportDocumentRef): string {
  if (d.exists === false) return "Создать заново";
  return "Перезаполнить";
}

const SOURCE_BADGES: Record<
  ResolveSource,
  { label: string; cls: string; tooltip: string }
> = {
  primary: {
    label: "auto",
    cls: "bg-emerald-100 text-emerald-700",
    tooltip: "Сопоставлено по mp_sku + mp_size (точное совпадение)",
  },
  artikul: {
    label: "auto (артикул)",
    cls: "bg-amber-100 text-amber-700",
    tooltip: "Сопоставлено по «Артикул поставщика» — задействован резервный канал",
  },
  barcode: {
    label: "auto (баркод)",
    cls: "bg-orange-100 text-orange-700",
    tooltip: "Сопоставлено по баркоду через регистр штрихкодов",
  },
  not_found: {
    label: "не найдено",
    cls: "bg-rose-100 text-rose-700",
    tooltip: "Товар не найден ни одним из способов",
  },
};

function SourceBadge({ source }: { source: ResolveSource }): React.ReactElement {
  const v = SOURCE_BADGES[source];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}
      title={v.tooltip}
    >
      {v.label}
    </span>
  );
}

function ResolvedTable({
  title,
  lines,
}: {
  title: string;
  lines: MpResolvedLine[];
}): React.ReactElement {
  if (lines.length === 0) {
    return (
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">— нет строк —</p>
      </section>
    );
  }
  const total = lines.reduce((s, l) => s + l.sum_total, 0);
  const qty = lines.reduce((s, l) => s + l.quantity, 0);
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">
        {title}{" "}
        <span className="text-muted-foreground">
          ({lines.length} групп / {qty} шт. / {formatRub(total)})
        </span>
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Номенклатура</TableHead>
            <TableHead>Характеристика</TableHead>
            <TableHead className="w-32 font-mono text-xs">SKU МП</TableHead>
            <TableHead className="w-32 font-mono text-xs">Артикул</TableHead>
            <TableHead className="w-24 text-right">Кол-во</TableHead>
            <TableHead className="w-32 text-right">Цена</TableHead>
            <TableHead className="w-32 text-right">Сумма</TableHead>
            <TableHead className="w-28">Источник</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((l, i) => (
            <TableRow key={`${l.nom_ref}|${l.char_ref ?? ""}`}>
              <TableCell className="text-xs text-muted-foreground">
                {i + 1}
              </TableCell>
              <TableCell className="text-sm">{l.nom_name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {l.char_name || "—"}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {l.sample_mp_sku ? (
                  <span
                    className={
                      l.source === "primary"
                        ? "font-semibold text-emerald-700"
                        : "text-muted-foreground"
                    }
                    title={
                      l.source === "primary"
                        ? "Резолв по SKU+размеру"
                        : "SKU есть в исходных данных, но резолв сработал по другому каналу"
                    }
                  >
                    {l.sample_mp_sku}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {l.sample_artikul ? (
                  <span
                    className={
                      l.source === "artikul"
                        ? "font-semibold text-amber-700"
                        : "text-muted-foreground"
                    }
                    title={
                      l.source === "artikul"
                        ? "Резолв по «Артикул поставщика»"
                        : "Артикул есть в исходных данных, но резолв сработал по другому каналу"
                    }
                  >
                    {l.sample_artikul}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">{l.quantity}</TableCell>
              <TableCell className="text-right">{formatRub(l.price)}</TableCell>
              <TableCell className="text-right font-medium">
                {formatRub(l.sum_total)}
              </TableCell>
              <TableCell>
                <SourceBadge source={l.source} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function B2bPanel({
  b2b,
}: {
  b2b: import("@/types/api").MpB2bPreviewBlock;
}): React.ReactElement {
  const hasMultiple = b2b.multiple_count > 0;
  return (
    <section className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800">
          B2B-покупатели ({b2b.kontragents.length})
        </h3>
        <p className="text-xs text-slate-600">
          Сумма B2B: {formatRub(b2b.summa_total)}
          {b2b.to_create_count > 0 && (
            <>
              {" · "}авто-создадим в УНФ:{" "}
              <span className="font-semibold">{b2b.to_create_count}</span>
            </>
          )}
          {hasMultiple && (
            <>
              {" · "}
              <span className="font-semibold text-amber-700">
                требуют ручной разбивки: {b2b.multiple_count}
              </span>
            </>
          )}
        </p>
      </div>
      {hasMultiple && (
        <p className="text-xs text-amber-800">
          В 1С найдено несколько контрагентов на один (ИНН, КПП) — кнопка
          «Создать» заблокирована. Очистите дубли в УНФ и обновите превью.
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ИНН</TableHead>
            <TableHead>КПП</TableHead>
            <TableHead>Наименование</TableHead>
            <TableHead>Первый СФ</TableHead>
            <TableHead className="text-right">Сумма</TableHead>
            <TableHead>Статус</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {b2b.kontragents.map((k) => (
            <TableRow key={`${k.inn}|${k.kpp ?? ""}`}>
              <TableCell className="font-mono text-xs">{k.inn}</TableCell>
              <TableCell className="font-mono text-xs">{k.kpp ?? "—"}</TableCell>
              <TableCell className="text-xs">{k.name ?? "—"}</TableCell>
              <TableCell className="text-xs">{k.nomer_sf_pervyi ?? "—"}</TableCell>
              <TableCell className="text-right">{formatRub(k.summa_total)}</TableCell>
              <TableCell className="text-xs">
                <B2bStatusBadge status={k.status} note={k.note} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}


function B2bStatusBadge({
  status,
  note,
}: {
  status: "mapped" | "missing" | "multiple" | "error";
  note: string | null;
}): React.ReactElement {
  const map = {
    mapped: { label: "✅ Найден", cls: "text-emerald-700" },
    missing: { label: "🟡 Будет создан", cls: "text-amber-700" },
    multiple: { label: "🔴 Дубли в 1С", cls: "text-rose-700" },
    error: { label: "⚠️ Ошибка", cls: "text-rose-700" },
  } as const;
  const m = map[status];
  return (
    <span className={m.cls} title={note ?? undefined}>
      {m.label}
    </span>
  );
}


function MissingPanel({
  missing,
}: {
  missing: MpMissingProduct[];
}): React.ReactElement | null {
  if (missing.length === 0) return null;
  return (
    <section className="space-y-2 rounded-md border border-rose-200 bg-rose-50 p-4">
      <h3 className="text-sm font-semibold text-rose-800">
        Не сопоставлено товаров: {missing.length}
      </h3>
      <p className="text-xs text-rose-700">
        Заведите эти товары в УНФ (с привязкой к маркетплейсу), затем обновите
        превью.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Код WB</TableHead>
            <TableHead>Размер</TableHead>
            <TableHead>Артикул</TableHead>
            <TableHead>Баркод</TableHead>
            <TableHead>Название</TableHead>
            <TableHead className="text-right">Строк</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {missing.map((m) => (
            <TableRow key={`${m.mp_sku ?? ""}|${m.mp_size}`}>
              <TableCell className="font-mono text-xs">{m.mp_sku ?? "—"}</TableCell>
              <TableCell>{m.mp_size}</TableCell>
              <TableCell className="text-xs">{m.artikul ?? "—"}</TableCell>
              <TableCell className="text-xs">{m.barcode ?? "—"}</TableCell>
              <TableCell className="text-xs">{m.name ?? "—"}</TableCell>
              <TableCell className="text-right">{m.rows}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

export function MarketplaceReportPreviewPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [dogovorKey, setDogovorKey] = useState<string>("");
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [incomingNumber, setIncomingNumber] = useState<string>("");
  const [incomingDate, setIncomingDate] = useState<string>("");

  const previewQ = useQuery({
    queryKey: ["mp-reports", id, "preview", connId],
    queryFn: () => getMpReportPreview(id!),
    enabled: !!id && !!connId,
  });

  // Преднастройки из preview — только если поле ещё пусто (юзер может
  // перетереть, и мы не должны откатывать его выбор при рефетче данных).
  useEffect(() => {
    const data = previewQ.data;
    if (!data) return;
    if (!dogovorKey && data.selected_dogovor_key) {
      setDogovorKey(data.selected_dogovor_key);
    }
    if (!periodStart) setPeriodStart(isoToDateInput(data.period_start));
    if (!periodEnd) setPeriodEnd(isoToDateInput(data.period_end));
    if (!incomingDate) {
      setIncomingDate(isoToDateInput(data.suggested_date) || isoToDateInput(data.period_end));
    }
    if (!incomingNumber && data.suggested_incoming_number) {
      setIncomingNumber(data.suggested_incoming_number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewQ.data]);

  const createBody = () => ({
    dogovor_key: dogovorKey,
    incoming_document_number: incomingNumber,
    incoming_document_date: incomingDate,
    period_start: periodStart,
    period_end: periodEnd,
  });

  // Текст одной строки документа для тоста: "№ X (проведён|не проведён)".
  function docLine(d: { kind: string; one_c_number: string | null; posted: boolean }) {
    const prefix = d.kind === "commission_writeoff" ? "Списание " : "";
    const num = d.one_c_number ?? "—";
    const status = d.posted ? "проведён" : "НЕ проведён";
    return `${prefix}№ ${num} — ${status}`;
  }

  // Достаёт текст ошибки из axios-исключения. Используется и в `toast`,
  // и в персистентной панели — чтобы пользователь не ловил тост глазами.
  function extractErrorDetail(err: unknown): string {
    return (
      (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
      String(err)
    );
  }

  // Тост по результату создания/перезаполнения: success если всё проведено,
  // warning если что-то не провелось (тогда post_error идёт в description).
  function toastResult(verb: string, resp: { documents: MpReportDocumentRef[] }) {
    const lines = resp.documents.map(docLine).join("; ");
    const errored = resp.documents.filter((d) => !d.posted);
    if (errored.length === 0) {
      toast.success(`${verb}: ${lines}`);
      return;
    }
    const errDesc = errored
      .map((d) => `${d.one_c_number ?? "—"}: ${d.post_error ?? "1С не вернула причину"}`)
      .join("\n");
    toast.warning(`${verb}, но НЕ ПРОВЕДЕН: ${lines}`, {
      description: errDesc,
      duration: 15000,
    });
  }

  const createMut = useMutation({
    mutationFn: () => createMpReportInUnf(id!, createBody()),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["mp-reports"] });
      toastResult("Документ создан в УНФ", resp);
    },
    onError: (err: unknown) => {
      toast.error("Не удалось создать документ", {
        description: extractErrorDetail(err),
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (kind: string) => updateMpReportInUnf(id!, kind, createBody()),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["mp-reports"] });
      qc.invalidateQueries({ queryKey: ["mp-reports", id] });
      toastResult("Документ перезаполнен в УНФ", resp);
    },
    onError: (err: unknown) => {
      toast.error("Не удалось перезаполнить документ", {
        description: extractErrorDetail(err),
      });
    },
  });

  if (previewQ.isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">Загрузка превью…</p>;
  }
  if (previewQ.isError) {
    const detail =
      (previewQ.error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail ?? String(previewQ.error);
    return (
      <div className="space-y-3 p-6">
        <Button variant="outline" onClick={() => navigate("/mp-reports")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          К списку
        </Button>
        <p className="text-sm text-rose-600">Ошибка: {detail}</p>
      </div>
    );
  }
  const data = previewQ.data;
  if (!data) return <p className="p-6">Нет данных</p>;

  const hasCreated = data.created_documents.length > 0;
  // Перезаполнить можно ЛЮБОЙ документ (проведённый авто-распровод, потом
  // PATCH, потом провести; непроведённый — PATCH+post; удалённый — POST
  // нового). Поэтому форма параметров остаётся видимой всегда, когда есть
  // документы.
  // `allHealthy` имеет смысл только в блоке `{hasCreated && ...}` ниже —
  // там Array.every на гарантированно непустом массиве. Не дублируем
  // `hasCreated &&` внутри условия.
  const allHealthy = data.created_documents.every(
    (d) => d.posted_in_1c === true && !d.post_error,
  );
  const formReady =
    !!dogovorKey &&
    !!incomingNumber.trim() &&
    !!incomingDate &&
    !!periodStart &&
    !!periodEnd &&
    data.is_ready;

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <Button variant="outline" size="sm" onClick={() => navigate("/mp-reports")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            К списку
          </Button>
          <h1 className="mt-3 text-xl font-semibold tracking-tight">
            Превью отчёта о реализации
          </h1>
        </div>
      </header>

      {data.parse_error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Ошибка парсинга: {data.parse_error}
        </div>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Организация" value={data.organization_name} />
        <Field
          label="Контрагент"
          value={data.kontragent_name ?? "не найден в 1С"}
          tone={data.kontragent_key ? undefined : "warn"}
        />
      </section>

      {hasCreated && (
        <div
          className={`rounded-md border p-4 text-sm ${
            allHealthy
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
        >
          <p className="font-medium">
            {allHealthy
              ? "Документ(ы) уже созданы и проведены в 1С:"
              : "Документ(ы) созданы в 1С:"}
          </p>
          <ul className="mt-2 space-y-2">
            {data.created_documents.map((d) => {
              const status = describeStatus(d);
              const pendingThis =
                updateMut.isPending && updateMut.variables === d.kind;
              return (
                <li
                  key={d.kind}
                  className="flex flex-col gap-y-1"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    {d.kind === "commission_writeoff"
                      ? "Отчёт о списании"
                      : "Отчёт комиссионера"}
                    : № {d.one_c_number ?? "—"}
                  </span>
                  <span className={`text-xs ${status.cls}`}>({status.label})</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      !formReady || updateMut.isPending || createMut.isPending
                    }
                    onClick={() => {
                      if (d.posted_in_1c === true) {
                        const ok = window.confirm(
                          `Документ № ${d.one_c_number ?? "—"} проведён в 1С. ` +
                            "Перезаполнение СНИМЕТ проведение, перезапишет шапку " +
                            "и табличную часть, потом снова проведёт. Движения по " +
                            "регистрам будут пересчитаны. Продолжить?",
                        );
                        if (!ok) return;
                      }
                      updateMut.mutate(d.kind);
                    }}
                    title={
                      formReady
                        ? d.exists === false
                          ? "Документ в 1С не найден — будет создан новый POST'ом"
                          : d.posted_in_1c === true
                          ? "Распровести + PATCH + провести заново"
                          : "Обновить шапку и табличную часть PATCH'ем"
                        : "Заполните договор/номер/дату/период в форме ниже"
                    }
                  >
                    {pendingThis
                      ? d.exists === false
                        ? "Создание…"
                        : "Обновление…"
                      : updateButtonLabel(d)}
                  </Button>
                  </div>
                  {d.post_error && (
                    <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800">
                      <span className="font-medium">Ошибка проведения в 1С:</span>{" "}
                      <span className="font-mono whitespace-pre-wrap">{d.post_error}</span>
                    </div>
                  )}
                  {updateMut.isError && updateMut.variables === d.kind && (
                    <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800">
                      <span className="font-medium">Ошибка перезаполнения:</span>{" "}
                      <span className="font-mono whitespace-pre-wrap">
                        {extractErrorDetail(updateMut.error)}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs">
            «Перезаполнить» обновляет тот же документ в 1С: шапка и табличная
            часть пересчитываются с актуальным FIFO-распределением по Партиям/
            Заказам, номер/Ref_Key не меняются. Если документ <b>проведён</b>,
            он будет автоматически распроведён, перезаписан и снова проведён
            (с подтверждением). Если документ <b>удалён</b> вручную в 1С —
            кнопка становится «Создать заново» и создаёт новый документ;
            локальная запись подхватывает свежий номер. Документы
            перезаполняются по отдельности.
          </p>
        </div>
      )}

      {/* Форма параметров видна всегда — для создания (если документов нет)
          или для перезаполнения существующих. */}
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="dogovor">Договор</Label>
            <select
              id="dogovor"
              className={SELECT_CLASSES}
              value={dogovorKey}
              onChange={(e) => setDogovorKey(e.target.value)}
              disabled={data.dogovors.length === 0}
            >
              <option value="">
                {data.dogovors.length === 0
                  ? "— нет договоров для контрагента —"
                  : "— выберите —"}
              </option>
              {data.dogovors.map((d) => (
                <option key={d.ref_key} value={d.ref_key}>
                  {d.description}
                  {d.vid_dogovora ? ` · ${d.vid_dogovora}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="incoming-number">Номер отчёта о реализации</Label>
            <Input
              id="incoming-number"
              type="text"
              placeholder="например, 693107216"
              value={incomingNumber}
              onChange={(e) => setIncomingNumber(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="incoming-date">Дата отчёта о реализации</Label>
            <Input
              id="incoming-date"
              type="date"
              value={incomingDate}
              onChange={(e) => setIncomingDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Будет также датой документа в УНФ
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="period-start">Период с</Label>
            <Input
              id="period-start"
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="period-end">Период по</Label>
            <Input
              id="period-end"
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>
      </section>

      <section className="rounded-md border bg-slate-50 p-4">
        <h3 className="mb-2 text-sm font-semibold">Итоги</h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Total label="Продажи" qty={data.totals.sales_qty} sum={data.totals.sales_sum} />
          <Total label="Возвраты" qty={data.totals.returns_qty} sum={data.totals.returns_sum} />
          <Total label="Списание" qty={data.totals.writeoff_qty} sum={data.totals.writeoff_sum} />
          <Total label="Сумма документа" sum={data.totals.document_total} bold />
        </div>
      </section>

      <MissingPanel missing={data.missing} />

      {data.b2b && <B2bPanel b2b={data.b2b} />}

      <ResolvedTable title="Продажи" lines={data.buckets.sales} />
      <ResolvedTable title="Возвраты" lines={data.buckets.returns} />
      <ResolvedTable title="Списание (компенсации)" lines={data.buckets.writeoff} />

      {!hasCreated && (
        <div className="sticky bottom-0 -mx-6 flex items-center justify-between gap-3 border-t bg-background/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <p className="text-xs text-muted-foreground">
            {disabledReason(
              data.is_ready,
              dogovorKey,
              incomingNumber,
              incomingDate,
              periodStart,
              periodEnd,
              data.missing.length,
            )}
          </p>
          <Button
            disabled={!formReady || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            <Rocket className="mr-2 h-4 w-4" />
            {createMut.isPending ? "Создание…" : "Создать в 1С УНФ"}
          </Button>
          {createMut.isError && (
            <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <div className="font-medium">Не удалось создать документ</div>
              <div className="mt-1 whitespace-pre-wrap font-mono text-xs">
                {extractErrorDetail(createMut.error)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function disabledReason(
  isReady: boolean,
  dogovorKey: string,
  incomingNumber: string,
  incomingDate: string,
  periodStart: string,
  periodEnd: string,
  missingCount: number,
): string {
  if (!isReady) {
    if (missingCount > 0) {
      return `Не сопоставлено товаров: ${missingCount}. Заведите их в УНФ + связку с маркетплейсом, потом обновите превью.`;
    }
    return "Не найден шаблон-документ или контрагент в 1С — превью пока не готово.";
  }
  const missing: string[] = [];
  if (!dogovorKey) missing.push("выберите договор");
  if (!incomingNumber.trim()) missing.push("введите номер отчёта");
  if (!incomingDate) missing.push("укажите дату отчёта");
  if (!periodStart || !periodEnd) missing.push("укажите период");
  if (missing.length === 0) return "Всё готово к созданию документа.";
  return "Перед созданием: " + missing.join(", ") + ".";
}

function Field({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn";
}): React.ReactElement {
  return (
    <div className="rounded-md border bg-white p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={`mt-1 text-sm font-medium ${
          tone === "warn" ? "text-rose-700" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Total({
  label,
  qty,
  sum,
  bold,
}: {
  label: string;
  qty?: number;
  sum: number;
  bold?: boolean;
}): React.ReactElement {
  return (
    <div className="rounded bg-white p-3 shadow-sm">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 ${bold ? "text-base font-semibold" : "text-sm"}`}>
        {formatRub(sum)}
      </p>
      {qty !== undefined && (
        <p className="text-xs text-muted-foreground">{qty} шт.</p>
      )}
    </div>
  );
}
